
import API_BASE_URL from '../config';
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('user');
    return saved ? JSON.parse(saved) : null;
  });
  const [token, setToken] = useState(() => localStorage.getItem('token') || null);
  const [isEditDisabled, setIsEditDisabled] = useState(() => {
    const role = localStorage.getItem('role');
    return role === 'user';
  });

  const login = async (username, password) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ email: username, password }),
      });
      if (!res.ok) return false;
      const data = await res.json();
      setUser(data.user);
      setToken(data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('token', data.token);
      if (data.user && data.user.name) {
        localStorage.setItem('name', data.user.name);
      }
      if (data.user && data.user.role) {
        localStorage.setItem('role', data.user.role);
        setIsEditDisabled(data.user.role === 'user');
      } else {
        localStorage.removeItem('role');
        setIsEditDisabled(false);
      }
      return true;
    } catch (e) {
      return false;
    }
  };

  const logout = async () => {
    if (token) {
      await fetch(`${API_BASE_URL}/api/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });
    }
    setUser(null);
    setToken(null);
    setIsEditDisabled(false);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('role');
  };

  // Automatic logout when token expires (JWT exp) and global 401 handler
  const logoutInProgressRef = useRef(false);
  const expiryTimerRef = useRef(null);
  const originalFetchRef = useRef(null);

  const scheduleLogoutFromToken = (tok) => {
    // clear previous timer
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
    if (!tok) return;
    try {
      const parts = tok.split('.');
      if (parts.length !== 3) return;
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      if (!payload.exp) return;
      const msUntilExp = payload.exp * 1000 - Date.now();
      // schedule a little before expiry (5s) to ensure logout occurs
      const delay = Math.max(0, msUntilExp - 5000);
      expiryTimerRef.current = setTimeout(async () => {
        if (!logoutInProgressRef.current) {
          logoutInProgressRef.current = true;
          try { await logout(); } catch (e) { /* ignore */ }
          // force navigation to login
          try { window.location = '/login'; } catch (e) { /* ignore */ }
        }
      }, delay);
    } catch (e) {
      // ignore parse errors
    }
  };

  // schedule whenever token changes
  useEffect(() => {
    scheduleLogoutFromToken(token);
    return () => {
      if (expiryTimerRef.current) {
        clearTimeout(expiryTimerRef.current);
        expiryTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // global fetch interceptor to logout on 401 responses
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (originalFetchRef.current) return; // already patched
    originalFetchRef.current = window.fetch.bind(window);
    window.fetch = async (...args) => {
      const res = await originalFetchRef.current(...args);
      try {
        if (res && res.status === 401) {
          // avoid recursion
          if (!logoutInProgressRef.current) {
            logoutInProgressRef.current = true;
            try { await logout(); } catch (e) { /* ignore */ }
            try { window.location = '/login'; } catch (e) { /* ignore */ }
          }
        }
      } catch (e) {
        // swallow
      }
      return res;
    };
    return () => {
      if (originalFetchRef.current) {
        window.fetch = originalFetchRef.current;
        originalFetchRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout, token, isEditDisabled }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
